import PropTypes from "prop-types";
import { Grid, Card, CardContent, Typography, Box } from "@mui/material";
import { Pie } from "react-chartjs-2";
import {
  BarChart,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
  Legend,
  Label,
} from "recharts";
import { useTranslation } from "react-i18next";
import FTEGapAnalysisChart from "./FTEGapAnalysis";
import AnnualSalaryPerJobChart from "./annualSalary";
import { memo, useEffect, useState } from "react";
import EmployeeChart from "./EmployeeChart";
import {
  CostPerFteChart,
  OpexRevenueChart,
  StaffingBalanceChart,
  WorkloadDistributionChart,
} from "./Charts";

const ManpowerAnalysisChart = ({
  workloadRechartData,
  staffingData,
  revenueData,
  nonRevenueData,
  scenario,
  scenarioJobs,
  costPerFteData,
  opexRevenueData,
  revenueSalaryData,
  nonRevenueSalaryData,
  data,
  emps,
}) => {
  const { t } = useTranslation();
  const [selectedJob, setSelectedJob] = useState(null);
  useEffect(() => {
    if (selectedJob) {
      if (emps.some((e) => e.jobId === selectedJob || e.id === selectedJob)) {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        });
      }
    }
  }, [selectedJob]);
  return (
    <>
      <StaffingBalanceChart t={t} staffingData={staffingData} />
      <WorkloadDistributionChart t={t} workloadRechartData={workloadRechartData} />

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="subtitle1">
              {t("user.manpowerPlanningAnalysis.fteGapAnalysis")}
            </Typography>
            <Grid container spacing={2}>
              {scenario?.type !== "GAHAR Phase 1" && (
                <Grid item xs={6}>
                  <FTEGapAnalysisChart
                    title={t("user.manpowerPlanningAnalysis.revenueGenerators")}
                    chartData={revenueData}
                    data={data}
                    setSelectedJob={setSelectedJob}
                  />
                </Grid>
              )}

              <Grid item xs={scenario?.type !== "GAHAR Phase 1" ? 6 : 12}>
                <FTEGapAnalysisChart
                  title={
                    scenario?.type === "GAHAR Phase 1"
                      ? undefined
                      : t("user.manpowerPlanningAnalysis.nonRevenueGenerators")
                  }
                  chartData={nonRevenueData}
                  data={data}
                  setSelectedJob={setSelectedJob}
                  scenario={scenario}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {scenarioJobs && scenario && scenario?.type !== "GAHAR Phase 1" && (
        <>
          <CostPerFteChart t={t} costPerFteData={costPerFteData} scenario={scenario} />
          <OpexRevenueChart t={t} opexRevenueData={opexRevenueData} />
        </>
      )}

      {emps && data && scenario?.type !== "GAHAR Phase 1" && (
        <EmployeeChart
          emps={emps}
          data={data}
          selectedJob={selectedJob}
          setSelectedJob={setSelectedJob}
        />
      )}
    </>
  );
};

ManpowerAnalysisChart.propTypes = {
  workloadRechartData: PropTypes.array.isRequired,
  staffingData: PropTypes.object.isRequired,
  revenueData: PropTypes.object.isRequired,
  nonRevenueData: PropTypes.object.isRequired,
  scenario: PropTypes.object.isRequired,
  scenarioJobs: PropTypes.array.isRequired,
  costPerFteData: PropTypes.array.isRequired,
  opexRevenueData: PropTypes.array.isRequired,
  revenueSalaryData: PropTypes.array.isRequired, // array of objects, not strictly checked
  nonRevenueSalaryData: PropTypes.array.isRequired,
  emps: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
};

export default memo(ManpowerAnalysisChart);
